import * as priority from 'priority-web-sdk';
import {InputFieldsReturn, EditFieldReturn} from 'priority-web-sdk';
import {noop} from '@priority-software/priority-style-react/dist';
import {procEname, procType} from '../chartConsts';
import {reportGeneratorsStore} from '../stores/ReportGeneratorsStore';
import {getLoginConfig} from '../../../services/proto/core/server';
import {getCompanyName} from '../../../utils/proto-api-utils/utils';
import {getDataplotOperator, getSerializedDataplotValues, getDataplotValue} from '../serialize';

export class ReportWebSdkService {
    activeForms = {};

    userLogin = async () => {
        try {
            await priority.login(getLoginConfig());
        } catch (e) {
            throw Error(e as string);
        }
    };

    procReportOptions = async data => {
        try {
            const reportOptions = await data.proc.reportOptions(data);
            return reportOptions;
        } catch (e) {
            console.log('An error occurred while trying to get report options', e);
        }
    };

    procInputOptions = async data => {
        try {
            const explantoryReport = data.input?.Options?.find(
                report =>
                    report.title === reportGeneratorsStore?.currentScreen?.getCurrExpRepTitle()
            );
            const inputOptions = await data.proc.inputOptions(1, explantoryReport.field);
            return inputOptions;
        } catch (e) {
            console.log('An error occurred while trying to get input options', e);
        }
    };

    procInputFields = async data => {
        const {currentScreen} = reportGeneratorsStore;
        try {
            const {getReportParams, getExpReportInputsMetaData, getCurrPlotValues} = currentScreen;
            const reportParams = getReportParams();
            const inputsMetaData = getExpReportInputsMetaData();
            const dimensionsValues = getCurrPlotValues();

            const returnData: EditFieldReturn[] = data.input.EditFields.map((input, idx) => {
                const inputIndex = inputsMetaData?.[idx]?.inputIndex;
                if (inputsMetaData?.[idx]?.isDataplot) {
                    const operator = getDataplotOperator(
                        inputsMetaData[idx].dateProps.dateFormat,
                        data.input.Operators
                    );
                    const value = getDataplotValue(dimensionsValues, inputsMetaData[idx]);
                    const values = getSerializedDataplotValues(
                        inputsMetaData[idx].dateProps,
                        value
                    );
                    const res = {
                        field: input.field,
                        op: operator.op,
                        op2: operator.op,
                        value: values.value,
                        value2: values.value2
                    };
                    return res;
                } else if (inputIndex >= 0) {
                    return {
                        field: input.field,
                        op: reportParams[inputIndex].OriginalOp,
                        op2: reportParams[inputIndex].OriginalOp2,
                        value: reportParams[inputIndex].Values[0],
                        value2: reportParams[inputIndex].Values[1] || ''
                    };
                } else {
                    const equalOperator = getDataplotOperator(null, data.input.Operators);
                    const res = {
                        field: input.field,
                        value: '',
                        op: equalOperator.op,
                        value2: '',
                        op2: equalOperator.op
                    };
                    return res;
                }
            });
            const inputFieldsReturn: InputFieldsReturn = {EditFields: returnData};
            const inputFieldsRes = await data.proc.inputFields(1, inputFieldsReturn);
            return inputFieldsRes;
        } catch (e) {
            console.log('An error occurred while trying to get input fields', e);
        }
    };

    openReportUrl = async data => {
        const {Urls} = data;
        if (Urls?.[0].action === 'display' && Urls[0].url.length) {
            const newWin = window.open(encodeURI(Urls[0].url), '_blank');
            if (!newWin || newWin.closed || typeof newWin.closed === 'undefined') {
                throw new Error('Popup blocked');
            }
        }
        await data.proc.continueProc();
    };

    procError = async (reason, data) => {
        console.log('Error:', reason);
        await data.proc.cancel();
    };

    actionSuccess = async data => {
        const {type} = data;
        try {
            if (type === 'reportOptions') {
                const reportOptionsRes = await this.procReportOptions(data);
                await this.actionSuccess(reportOptionsRes);
            } else if (type === 'inputOptions') {
                const inputOptionsRes = await this.procInputOptions(data);
                await this.actionSuccess(inputOptionsRes);
            } else if (type === 'inputFields') {
                const inputFieldsRes = await this.procInputFields(data);
                await this.actionSuccess(inputFieldsRes);
            } else if (type === 'displayUrl') {
                this.openReportUrl(data);
            }
        } catch (e) {
            await this.procError(e, data);
        }
    };

    startProc = async () => {
        try {
            await this.userLogin();
            const currCompany = getCompanyName();
            const activateRes = await priority.procStart(procEname, procType, noop, currCompany);
            await this.actionSuccess(activateRes);
        } catch (e) {
            console.log('An error occurred while trying to start report', e);
        }
    };
}

export const reportWebSdkService = new ReportWebSdkService();
